import pluralize from "pluralize";

import {
  CATEGORY_ESCAPE_ROOM,
  CATEGORY_HAUNT,
} from "@/components/game-categories/constants";
import { locationName } from "@/helpers/games";

import type { LocationType } from "../types";

function getRenderLocation(location: LocationType): string {
  if (!location.games.some((game) => game.kind !== "VER")) {
    return "ONLINE";
  }

  const neighborhood = locationName(location.address, "neighborhood", true);
  const country = locationName(location.address, "country");
  const city = locationName(location.address, "locality");
  const state = locationName(
    location.address,
    "administrative_area_level_1",
    true
  );

  const nameParts: string[] = [];

  if (neighborhood) {
    nameParts.push(neighborhood);
  }
  if (city) {
    nameParts.push(city);
  }
  if (country === "United States") {
    if (state) {
      nameParts.push(state);
    }
  } else if (country) {
    nameParts.push(country);
  }

  return nameParts.join(", ");
}

function getAddressDescription(location: LocationType): string[] {
  const parts: string[] = [];

  const route = location.address.address_components.find(({ types }) =>
    types.includes("route")
  );

  if (route) {
    const streetNumber = location.address.address_components.find(({ types }) =>
      types.includes("street_number")
    );
    const subpremise = location.address.address_components.find(({ types }) =>
      types.includes("subpremise")
    );

    parts.push("located at");
    if (streetNumber) {
      parts.push(streetNumber.short_name);
    }
    parts.push(route.short_name);
    if (subpremise) {
      parts.push(`#${subpremise.short_name}`);
    }
  }

  return parts;
}

export function getLocationMetaProps(location: LocationType) {
  const games = location.games;

  const primaryCategories = games.map(
    (game) => game.primaryCategory?.parent?.slug
  );

  const gamesCount = games.length;
  const isAllEscapeRooms = primaryCategories.every(
    (category) => category === CATEGORY_ESCAPE_ROOM
  );
  const isAllHaunts = primaryCategories.every(
    (category) => category === CATEGORY_HAUNT
  );

  const renderLocation = getRenderLocation(location);

  const metaDescription: string[] = [];

  metaDescription.push(location.displayName || location.company.name);

  if (isAllEscapeRooms) {
    metaDescription.push("is an escape room in");
  } else if (isAllHaunts) {
    metaDescription.push("is a haunted attraction in");
  } else {
    metaDescription.push("in");
  }

  metaDescription.push(`${renderLocation}, with`);
  metaDescription.push(gamesCount.toString());
  metaDescription.push(pluralize("experience", gamesCount));
  metaDescription.push(...getAddressDescription(location));

  const description = `${metaDescription.join(" ")}.`;

  const title = `${location.displayName || location.company.name} - ${location.locality.locality}, ${location.locality.region}`;

  return { description, title };
}
